import React from 'react';
import { NextPage } from 'next';
import Head from 'next/head';

import { EExpand } from '@r1-frontend/api-domru/api-content/_dto/tariff-lines-common-types';
import getTariffLines, {
    ResponseDto as GetTariffLinesResponseDto,
} from '@r1-frontend/api-domru/api-content/v1/tariff-lines';
import { getArticlesByPlace } from '@r1-frontend/api-domru/content-static/v2/articles';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { getBanners } from '~/src/api/content/banners';
import { FaqResponse } from '~/src/components/Questions/types';
import { ITariffLine } from '~/src/entities/tariff-lines';
import Layout from '~/src/features/_layout';
import { SeoScriptAggregateRatingWithOffer } from '~/src/features/_layout/SeoScripts';
import Bundles from '~/src/features/bundles';
import { getMinCostTariffInTariffLines } from '~/src/features/bundles/helpers/getMinCostTariffInTariffLines';
import { getAccessModeByRights } from '~/src/helpers/accessMode';
import { initialProps } from '~/src/helpers/basePageFunc';
import { getCookie } from '~/src/helpers/cookie';
import getRequester from '~/src/helpers/getRequester';
import { saveBanner, saveTariffLinesAvailableOnBundles } from '~/src/store/actions/bundles';
import { SET_QA_ARTICLES } from '~/src/store/reducers/faq';
import { selectCompanyName } from '~/src/store/selectors/city';
import selectProviderId from '~/src/store/selectors/selectProviderId';

type TBundlesPageProps = {
    city: ICurrentCity,
    companyName: string,
    tariffLines: ITariffLine[],
    isVillage: boolean,
}


const BundlesPage: NextPage<TBundlesPageProps> = ({ city, companyName, tariffLines, isVillage }): JSX.Element => {
    const props = {
        title: isVillage
            ? `Подключить ТВ и интернет в загородный дом в ${city.cityIn} | Цифровое телевидение на дачу`
            : `Домашний интернет и ТВ: тарифы от Дом.ру в ${city.cityIn} | Подключить интернет и телевидение`,
        description: isVillage
            ? `Подключите цифровое телевидение и скоростной интернет в загородный дом от провайдера ${companyName} в ${city.cityIn}.
                Стабильное соединение по технологиям GPON и WNGN в частном секторе. Подробнее на сайте 🏡`
            : `Быстрое подключение к домашнему интернету и цифровому телевидению в ${city.cityIn} от провайдера Дом.ру. 
                Выгодные пакеты тарифов. Высокая бесперебойность и надежность интернет-сети.`,
    };

    const minCostedTariff = getMinCostTariffInTariffLines(tariffLines, isVillage);


    return (
        <>
            {!isVillage && (
                <SeoScriptAggregateRatingWithOffer
                    {...minCostedTariff}
                    name={`Тарифы ${companyName} для квартиры`}
                    ratingCount={'1899'}
                    ratingValue={'4.6'}
                />
            )}

            <Head>
                <title>{props.title}</title>

                <meta name='description' content={props.description} />

                {isVillage && (
                    <meta name='robots' content='noindex, nofollow'/>
                )}
            </Head>

            <Layout
                withMenu={true}
                withChat={true}
                withFooter={true}
                bgColor={COLORS.BgMain}

                {...props}
            >
                <Bundles tariffLines={tariffLines} city={city} isVillage={isVillage} />
            </Layout>
        </>
    );
};

BundlesPage.getInitialProps = async(options) => {
    const { validatedAccessToken } = await initialProps(options, { isNeedAuthData: true });
    let isVillage = false;

    const { store, req, query: { alias } } = options;
    if (alias && alias.length && alias.length === 1 && alias[0] === 'village') {
        isVillage = true;
    }
    const state = store.getState();
    const doRequest = getRequester(req);


    // at the moment this life hack is the less evil
    // we could not use the getRequester method for requests because they implementation are old
    // if (read when) we change implementation of getBanners and getTariffLines we will use the getRequester method and remove this legacy implementation
    const providerId = Number(getCookie('providerId', req) || selectProviderId(state));

    const {
        city: { currentCity: city },
        tariffLines = [],
    } = state;
    const companyName = selectCompanyName(state);

    const bannersResponse = await getBanners({
        providerId,
        isAuth: !!validatedAccessToken,
        tag: 'bundles_new',
        expand: 'bannerInfo.advertising,bannerInfo.tags,order',
    });

    if (bannersResponse.isSuccess) {
        store.dispatch(saveBanner(bannersResponse.payload[0]?.bannerInfo));
    }

    // if tariff lines present on the store we should not make a request and set it data to the store again
    // this is necessary to share responsibility between the SSR and a browser render
    if (!tariffLines.length) {
        const _tariffLinesResponse = await doRequest.apiContent<GetTariffLinesResponseDto>(getTariffLines({
            expand: [EExpand.Tariffs, EExpand.MinPrice, EExpand.ShopItems, EExpand.ShopItemsProps],
            accessMode: getAccessModeByRights({}),
        }));

        if (_tariffLinesResponse.isSuccess) {
            const _tariffLines = _tariffLinesResponse.payload.items
                ?.filter(
                    tariffLine =>
                        tariffLine.available_on_bundles_page === 1 &&
                        tariffLine.minPrices.some(price => price.is_cottage === Number(isVillage)),
                );

            store.dispatch(saveTariffLinesAvailableOnBundles(_tariffLines));
        }
    }


    const faqResponse = await doRequest.apiContentStatic<FaqResponse>(getArticlesByPlace({ providerId,  placeAlias: 'bundles' }));

    store.dispatch({
        type: SET_QA_ARTICLES,
        payload: {
            placeAlias: 'bundles',
            faqArticles: faqResponse.isSuccess ? faqResponse.payload.items : [],
        },
    });

    return {
        city,
        companyName,
        isVillage,
        tariffLines: store.getState().bundles.tariffLines, // receipt from the state due to relevance at the moment
    };

};

export default BundlesPage;
